import React, { useMemo } from 'react'
import {
  Box,
  Typography,
  CircularProgress,
  InputAdornment,
  Alert,
  styled,
  Autocomplete,
  TextField,
} from '@mui/material'
import { Search } from '@mui/icons-material'
import Icon, { IconName } from 'atoms/Icon'
import { useMessagesDashboardViewContext } from '../MessagesDashboardViewContext'
import { ACTION_ICON_MAPPING as DEFAULT_ACTIONS_ICON_MAPPING } from 'common/constants'

export interface ActionOption {
  label: string
  value: string
  icon?: IconName
  description?: string
  group?: 'default' | 'custom'
}

export interface MessageActionSelectorProps {
  actions?: Array<{
    name: string
    key: string
    description?: string
  }>
  onActionChange?: (action: { name: string; key: string } | null) => void
  placeholder?: string
  minWidth?: number | string
  className?: string
  loading?: boolean
  error?: string
  disabled?: boolean
}

// Styled components to reduce repetitive sx props
const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}))

const StateDisplayBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  border: '1px dashed',
  borderColor: theme.palette.grey[300],
  borderRadius: theme.shape.borderRadius,
}))

// Helper components to reduce repetition
const StateDisplay: React.FC<{
  icon: IconName
  message: string
  loading?: boolean
}> = ({ icon, message, loading }) => (
  <StyledBox>
    <StateDisplayBox>
      {loading ? <CircularProgress size={20} /> : <Icon name={icon} size={20} />}
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </StateDisplayBox>
  </StyledBox>
)

export default function MessageActionSelector({
  actions,
  onActionChange,
  placeholder = 'Choose action...',
  minWidth = 400,
  className,
  loading = false,
  error,
  disabled = false,
}: MessageActionSelectorProps) {
  const context = useMessagesDashboardViewContext()

  // Define default action keys based on ACTION_ICON_MAPPING
  const defaultActionKeys = Object.keys(DEFAULT_ACTIONS_ICON_MAPPING)

  const actionsAsOptions = useMemo((): ActionOption[] => {
    if (!actions) return []

    return actions
      .map(a => ({
        label: a.name,
        value: a.key,
        icon: DEFAULT_ACTIONS_ICON_MAPPING[a.key] || 'cog',
        description: a.description,
        group: (defaultActionKeys.includes(a.key) ? 'default' : 'custom') as 'default' | 'custom',
      }))
      .sort((a, b) => {
        // Sort by group first (default actions first), then by label
        if (a.group !== b.group) {
          return a.group === 'default' ? -1 : 1
        }
        return a.label.localeCompare(b.label)
      })
  }, [actions])

  const handleActionChange = (event: any, newValue: any) => {
    if (newValue && typeof newValue === 'object' && 'value' in newValue) {
      const action = actions?.find(a => a.key === newValue.value)
      if (action) {
        context.selectMessageAction(action)
        onActionChange?.(action)
      }
    } else {
      context.selectMessageAction(null)
      onActionChange?.(null)
    }
  }

  const selectedAction = context.messagesActionWapper.action
  const selectedOption = selectedAction
    ? actionsAsOptions.find(opt => opt.value === selectedAction.key)
    : null

  // Early returns for different states
  if (loading) {
    return <StateDisplay icon="cog" message="Loading actions..." loading />
  }

  if (error) {
    return (
      <StyledBox>
        <Alert severity="error" sx={{ fontSize: '0.875rem' }}>
          {error}
        </Alert>
      </StyledBox>
    )
  }

  if (!actions || actions.length === 0) {
    return <StateDisplay icon="info" message="No actions available" />
  }

  return (
    <Box
      sx={{
        position: 'relative',
        flex: 1,
        minWidth,
      }}
    >
      <Autocomplete
        options={actionsAsOptions}
        value={selectedOption}
        onChange={handleActionChange}
        getOptionLabel={option => (option as ActionOption).label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        disabled={disabled}
        className={className || 'floating-action-select'}
        multiple={false}
        groupBy={option => (option as ActionOption).group || 'custom'}
        renderGroup={params => (
          <Box key={params.key}>
            <Typography
              variant="subtitle2"
              sx={{
                px: 2,
                py: 1,
                fontWeight: 600,
                color: 'text.secondary',
                backgroundColor: 'grey.50',
                borderBottom: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              {params.group === 'default' ? 'Default actions' : 'Custom actions'}
            </Typography>
            {params.children}
          </Box>
        )}
        renderInput={params => (
          <TextField
            {...params}
            placeholder={placeholder}
            error={!!error}
            size="small"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                borderRadius: 1,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: 'grey.50',
                },
                '&.Mui-focused': {
                  backgroundColor: 'white',
                },
              },
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, width: '100%' }}>
              <Icon name={option.icon || 'cog'} size={18} />
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {option.label}
                </Typography>
                {option.description && (
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                    {option.description}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}
        ListboxProps={{
          style: { maxHeight: 400 },
        }}
        noOptionsText="No actions found"
        loading={loading}
        loadingText="Loading actions..."
        clearOnBlur={false}
        selectOnFocus={false}
        openOnFocus={true}
        sx={{ width: '100%' }}
      />
    </Box>
  )
}
